import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { AvatarPair } from "../../components/Avatar.js";
import { ShareLink } from "../../components/ShareLink.js";
import { IconManage } from "../../components/icons.js";
import { iconButtonClass, iconButtonStyle } from "../../components/ui.js";
import { hasRole, useAuth } from "../../lib/auth.js";
import { fetchMyChildren } from "../../lib/members.js";
import { isPlayable } from "../../lib/draw.js";
import { formatScoreline } from "../../lib/matches.js";
import {
  areEntriesClosed,
  entryRefusal,
  formatDateRange,
  isKidsCategory,
  ordinal,
  registeredEntries,
  waitlistPosition,
  type DrawFixture,
  type Tournament,
  type TournamentEvent,
} from "../../lib/types.js";
import { EventSummary } from "../../components/EventSummary.js";
import { GroupTable, useGroupTables } from "./GroupTable.js";
import { offersSeeding, seedingHint } from "./EventSeeding.js";
import {
  EventGate,
  fixturesFor,
  rosterOf,
  sidePeople,
  useDraw,
  type Person,
  type Roster,
} from "./shared.js";

/**
 * The front door of an event: what it is, where you stand in it, and the way in to each of
 * the things you might have come for.
 *
 * It holds no controls of its own beyond the one that matters on the night — the score of
 * your next match. Everything else is a link, because everything else is a screenful.
 */
export function EventHub() {
  return (
    <EventGate>
      {({ tournament, event }) => <Hub tournament={tournament} event={event} />}
    </EventGate>
  );
}

function Hub({ tournament, event }: { tournament: Tournament; event: TournamentEvent }) {
  const { member } = useAuth();
  const memberId = member?.id ?? null;
  const canOrganise = hasRole(member, "organiser");

  const { data: fixtures = [] } = useDraw(event.id);
  const roster = rosterOf(event);

  /** A pool is entered but never drawn, so there is nothing behind the draw's three doors. */
  const pool = event.format === "auction_pool";

  const mine = memberId ? event.entries.find((entry) => entry.memberId === memberId) : undefined;
  const queuePlace = memberId ? waitlistPosition(event.entries, memberId) : null;

  // A kids' event is entered by a parent for a child, so the row speaks about the children —
  // "Your entry · Not registered" read to a parent as though the event were not for them.
  const kids = isKidsCategory(event.category);
  const { data: children = [] } = useQuery({
    queryKey: ["children", memberId],
    queryFn: () => fetchMyChildren(memberId!),
    enabled: kids && Boolean(memberId),
  });
  const childrenIn = children.filter((child) =>
    event.entries.some((entry) => entry.memberId === child.id),
  );

  // The one call to action a player who is not in yet came here for. Same rules as the
  // events list's stamp: entries open, the tournament not over, and somebody in the
  // household could enter — you by category, or, for a kids' event, a grown-up entering a
  // child. Rating bounds and the rest are the register screen's to explain.
  const finished =
    (tournament.endsOn ?? tournament.startsOn) < new Date().toLocaleDateString("en-CA");
  const open = !finished && !areEntriesClosed(event);
  // Somebody in the household has an entry to report. Without one, a closed event has no
  // entry row at all: it would only lead to a register screen that refuses.
  const entered = Boolean(mine) || childrenIn.length > 0;
  const full = event.capacity !== null && registeredEntries(event.entries).length >= event.capacity;
  const invite =
    !open || mine || !member
      ? null
      : kids
        ? !member.junior && childrenIn.length === 0
          ? "Enter your child"
          : null
        : entryRefusal(event.category, member) === null
          ? full
            ? "Join the queue"
            : "Register"
          : null;

  const played = fixtures.filter((fixture) => fixture.result);
  const yours = fixturesFor(fixtures, memberId);
  const next = yours.find((fixture) => isPlayable(fixture));

  // The table you are in — or, when there is only one, the table. With several groups and
  // you in none of them, the hub shows no table rather than guessing which one you want.
  const table = useGroupTables(event);
  const yourGroup =
    table.groups.find((group) => group.rows.some(table.isYours)) ??
    (table.groups.length === 1 ? table.groups[0] : undefined);

  const latest = [...played]
    .sort((x, y) => (y.result?.playedAt ?? "").localeCompare(x.result?.playedAt ?? ""))
    .slice(0, 3);

  return (
    <main className="mx-auto max-w-2xl px-4 pb-16 pt-6">
      <Link to={`/tournaments/${tournament.id}`} className="micro mb-4 inline-block underline">
        ← {tournament.name}
      </Link>

      {/* The actions ride on the eyebrow line rather than beside the title, so the name of
          the event keeps the full column. */}
      <header className="mb-8">
        <div className="-my-2.5 mb-1 flex items-center justify-between gap-3">
          {/* When and where, as the eyebrow — "Badminton" said nothing in a one-sport app,
              and this line used to sit under the title as a third grey caption. */}
          <div className="micro">
            {formatDateRange(tournament.startsOn, tournament.endsOn)}
            {tournament.venue ? ` · ${tournament.venue}` : ""}
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <ShareLink
              path={`/events/${event.id}`}
              label="Share this event"
              title={event.name}
              text={`${event.name} — ${tournament.name}. Register here:`}
            />
            {canOrganise ? (
              <Link
                to={`/events/${event.id}/manage`}
                aria-label="Manage this event"
                title="Manage this event"
                className={iconButtonClass}
                style={iconButtonStyle}
              >
                <IconManage />
              </Link>
            ) : null}
          </div>
        </div>

        <h1
          className="font-display uppercase"
          style={{ fontSize: "clamp(44px, 15vw, 60px)", fontWeight: 900, lineHeight: 0.84 }}
        >
          {event.name}
        </h1>
        <div className="mt-4">
          <EventSummary event={event} />
        </div>
        {/* Said to everybody, entered or not: the register screen is not the only place a
            closed event should read as closed. */}
        {open ? null : (
          <p className="font-mono mt-3 text-[11px] font-semibold uppercase tracking-[0.12em]">
            Registration closed
          </p>
        )}
      </header>

      {/* The one thing worth a button on a night out: the match you are about to play, and
          the score of the one you just did. Everything else on this screen is navigation. */}
      {next ? (
        <section
          aria-label="Your next match"
          className="mb-8 border-2"
          style={{ borderColor: "var(--color-line)" }}
        >
          <div
            className="font-mono flex h-[30px] items-center justify-between px-3 text-[11px] font-semibold uppercase tracking-[0.12em]"
            style={{ background: "var(--color-line)", color: "var(--color-ground)" }}
          >
            <span>Your next match</span>
            <span>#{next.sequence}</span>
          </div>
          <div className="flex flex-col gap-2 px-3.5 pt-3.5">
            <Contender people={sidePeople(next.playersA, roster)} />
            <span className="font-mono pl-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-muted">
              versus
            </span>
            <Contender people={sidePeople(next.playersB, roster)} />
          </div>
          <div className="p-3.5">
            {/* The link is the button: a <button> inside an <a> is two controls in one place,
                and the inner one swallows the tap on some phones. */}
            <Link
              to={`/events/${event.id}/score/${next.id}`}
              className="flex min-h-[52px] items-center justify-center gap-2.5 font-bold"
              style={{ background: "var(--color-line)", color: "var(--color-ground)" }}
            >
              Enter the score
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                aria-hidden
              >
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </Link>
          </div>
        </section>
      ) : null}

      {invite ? (
        <section aria-label="Entries are open" className="mb-8">
          <p className="font-mono mb-2.5 text-[11px] font-semibold uppercase tracking-[0.12em]">
            Entries open
            {event.entriesCloseAt ? ` · close ${closesLabel(event.entriesCloseAt)}` : ""}
            {full ? " · full, queue open" : ""}
          </p>
          {/* The link is the button, as on the next-match panel above. */}
          <Link
            to={`/events/${event.id}/register`}
            className="flex min-h-[52px] items-center justify-center gap-2.5 font-bold"
            style={{ background: "var(--color-line)", color: "var(--color-ground)" }}
          >
            {invite}
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              aria-hidden
            >
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </Link>
        </section>
      ) : null}

      <nav className="border-t-2" style={{ borderColor: "var(--color-line)" }}>
        {/* While the Register / Enter your child button above is showing, this row would
            be the same link a second time. It comes back once there is an entry to report.
            Once registration is closed it shows only to somebody with an entry. */}
        {invite || (!open && !entered) ? null : (
          <>
            {kids && !mine ? (
              <Row
                to={`/events/${event.id}/register`}
                label={
                  childrenIn.length === 0
                    ? "Enter your child"
                    : childrenIn.length === 1
                      ? "Your child's entry"
                      : "Your children's entries"
                }
                hint={
                  childrenIn.length > 0
                    ? `${childrenIn.map((child) => child.displayName.split(" ")[0]).join(", ")} entered`
                    : children.length > 0
                      ? "Not entered"
                      : "Add them here"
                }
              />
            ) : (
              <Row
                to={`/events/${event.id}/register`}
                label="Your entry"
                hint={
                  mine
                    ? mine.status === "registered"
                      ? "You're in"
                      : `${ordinal(queuePlace ?? 0)} in the queue`
                    : "Not registered"
                }
              />
            )}
          </>
        )}
        <Row
          to={`/events/${event.id}/entries`}
          label="Who's in"
          hint={`${registeredEntries(event.entries).length}`}
        />
        {pool ? null : (
          <>
            <Row
              to={`/events/${event.id}/draw`}
              label="Draw"
              hint={
                fixtures.length ? `${played.length} of ${fixtures.length} played` : "Not drawn yet"
              }
            />
            <Row
              to={`/events/${event.id}/results`}
              label="Results"
              hint={played.length ? `${played.length}` : "None yet"}
            />
            <Row to={`/events/${event.id}/standings`} label="Standings" hint="" />
          </>
        )}
        {canOrganise ? (
          <Row
            to={`/events/${event.id}/manage`}
            label="Organise"
            hint={pool ? "Entries" : "Teams and the draw"}
          />
        ) : null}
        {canOrganise ? <Row to={`/events/${event.id}/payments`} label="Payments" hint="" /> : null}
        {canOrganise && offersSeeding(event) ? (
          <Row to={`/events/${event.id}/seeding`} label="Seeding" hint={seedingHint(event)} />
        ) : null}
      </nav>

      {yourGroup ? (
        <section className="mt-8">
          <div className="font-mono mb-2 flex items-baseline justify-between gap-3 text-[11px] font-semibold uppercase tracking-[0.12em]">
            <h2>{yourGroup.pool !== null ? `Group ${yourGroup.pool}` : "The table"}</h2>
            {table.groups.length > 1 ? (
              <Link to={`/events/${event.id}/standings`} className="text-ink-muted underline">
                All groups
              </Link>
            ) : null}
          </div>
          <GroupTable
            group={yourGroup}
            isTie={table.isTie}
            label={table.label}
            faces={table.faces}
            isYours={table.isYours}
          />
        </section>
      ) : null}

      {latest.length > 0 ? (
        <section className="mt-8 border-t-2 pt-4" style={{ borderColor: "var(--color-line)" }}>
          <h2 className="font-mono mb-2 text-[11px] font-semibold uppercase tracking-[0.12em]">
            Latest scores
          </h2>
          {latest.map((fixture) => (
            <Result key={fixture.id} fixture={fixture} roster={roster} />
          ))}
          <Link to={`/events/${event.id}/results`} className="micro mt-3 inline-block underline">
            All results
          </Link>
        </section>
      ) : null}
    </main>
  );
}

/** Faces then names, the same shape the draw and the match log use. */
function Line({ people, beaten, prefix }: { people: Person[]; beaten: boolean; prefix?: string }) {
  return (
    <div
      className={`flex items-center gap-2 text-sm ${prefix ? "mt-1" : ""}`}
      style={beaten ? { color: "var(--color-ink-muted)" } : undefined}
    >
      <AvatarPair people={people} size={22} />
      <span className="truncate">
        {prefix}
        {people.map((person) => person.name).join(" & ")}
      </span>
    </div>
  );
}

function Row({ to, label, hint }: { to: string; label: string; hint: string }) {
  return (
    <Link
      to={to}
      className="flex items-center gap-3 border-b"
      style={{ borderColor: "var(--color-line-muted)", minHeight: 56 }}
    >
      <span className="flex-1 text-base font-semibold">{label}</span>
      <span className="font-mono shrink-0 text-[11px] text-ink-muted">{hint}</span>
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        aria-hidden
        className="shrink-0"
      >
        <path d="M9 5l7 7-7 7" />
      </svg>
    </Link>
  );
}

/** One side of your next match, as the scoreboard would post it. */
function Contender({ people }: { people: Person[] }) {
  return (
    <div className="flex items-center gap-2.5">
      <AvatarPair people={people} size={30} />
      <span className="font-display truncate text-[26px] uppercase" style={{ fontWeight: 800 }}>
        {people.map((person) => person.name).join(" & ")}
      </span>
    </div>
  );
}

/** One line of what happened. The results screen has its own, with more on it. */
function Result({ fixture, roster }: { fixture: DrawFixture; roster: Roster }) {
  const result = fixture.result;
  if (!result) return null;

  return (
    <div className="border-t py-2" style={{ borderColor: "var(--color-line-muted)" }}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <Line people={sidePeople(fixture.playersA, roster)} beaten={result.winner === "B"} />
          <Line
            people={sidePeople(fixture.playersB, roster)}
            beaten={result.winner === "A"}
            prefix="v "
          />
        </div>
        <span className="font-mono shrink-0 text-sm">{formatScoreline(result.games)}</span>
      </div>
    </div>
  );
}

/** "Wed 8 Oct, 9pm" — when entries close, in the reader's own time. */
function closesLabel(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
  });
}
